import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Truck,
  Plus,
  Minus,
  ChevronLeft,
  Home,
  Loader2,
  Clock,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  Wallet,
  History,
  HelpCircle,
  Bot,
  Info,
  CreditCard,
  Package,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { useNavigation } from '@/shared/hooks/useNavigation';
import { useOrgaConfigurationContext } from '@/app/providers';
import { Card, CardHeader, CardBody, Button } from '@/components/ui';
import SeoTags from '@/components/shared/SeoTags';
import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { BuyCreditsModal } from '@/features/admin/components/BuyCreditsModal';

// Types for unified credits
interface CreditBalance {
  organizationId: string;
  balanceCents: number;
  balanceEur: string;
  totalPurchasedCents: number;
  totalUsedCents: number;
  totalUsedForShippingCents: number;
  totalUsedForAICents: number;
}

interface CreditTransaction {
  id: string;
  organizationId: string;
  type: 'purchase' | 'usage' | 'refund';
  usageType?: 'shipping' | 'ai';
  amountCents: number;
  amountEur: string;
  balanceAfterCents: number;
  balanceAfterEur: string;
  description: string;
  orderIds?: string[];
  parcelsCreated?: number;
  aiUsageDetails?: {
    sessionId?: string;
    promptTokens?: number;
    completionTokens?: number;
    model?: string;
  };
  createdAt: { _seconds: number };
}

type UsageTypeFilter = 'all' | 'shipping' | 'ai';

// Helpers
const formatCurrency = (cents: number) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
};

const formatDate = (timestamp: { _seconds: number }) => {
  const date = new Date(timestamp._seconds * 1000);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Format AI transaction description to only show duration
 * Removes token count, suggestions count, and amount in brackets (amount is shown separately)
 */
const formatAIDescription = (description: string): string => {
  // Match patterns like "Assistant IA Live - Session: X tokens, Y suggestions (Z min) (0.0024€)"
  // We want to extract: "Assistant IA Live - Session (Z min)"

  // Remove the amount in brackets at the end (e.g., "(0.0024€)")
  let formatted = description.replace(/\s*\([0-9.,]+\s*€\)\s*$/i, '');

  // Remove the token count (e.g., "1800 tokens, " or "1800 tokens")
  formatted = formatted.replace(/\d+\s*tokens?,?\s*/i, '');

  // Remove the suggestions count (e.g., "3 suggestions, " or "3 suggestions")
  formatted = formatted.replace(/\d+\s*suggestions?,?\s*/i, '');

  // Clean up any double spaces, trailing commas, or empty colons
  formatted = formatted.replace(/,\s*,/g, ',').replace(/:\s*,/g, ':').replace(/:\s*\(/g, ' (').replace(/\s+/g, ' ').trim();

  // Remove trailing colon if nothing follows
  formatted = formatted.replace(/:\s*$/, '');

  return formatted;
};

// Removed PurchaseForm - now using BuyCreditsModal component

// Main Page Component - Renamed to CreditsPage but keeping file name for backward compatibility
export const ShippingCreditsPage: React.FC = () => {
  const { t } = useTranslation();
  const { idorga } = useParams<{ idorga: string }>();
  const { organizationId } = useNavigation();
  const { configuration, loading: configLoading } = useOrgaConfigurationContext();

  // State
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [initialPurchaseAmount, setInitialPurchaseAmount] = useState<number | undefined>(undefined);
  const [showInfoCard] = useState(true);
  const [usageTypeFilter, setUsageTypeFilter] = useState<UsageTypeFilter>('all');

  // Check if Stripe Connect is enabled
  const stripeConnectEnabled = !!configuration?.stripeConnectConfig?.accountId;

  // Use real-time balance from configuration (updates automatically via Firestore listener)
  const creditsBalance = configuration?.creditsBalance;
  const balance: CreditBalance | null = creditsBalance ? {
    organizationId: organizationId || '',
    balanceCents: creditsBalance.balanceCents ?? 0,
    balanceEur: formatCurrency(creditsBalance.balanceCents ?? 0),
    totalPurchasedCents: creditsBalance.totalPurchasedCents ?? 0,
    totalUsedCents: creditsBalance.totalUsedCents ?? 0,
    totalUsedForShippingCents: creditsBalance.totalUsedForShippingCents ?? 0,
    totalUsedForAICents: creditsBalance.totalUsedForAICents ?? 0,
  } : null;
  const loading = configLoading;

  // Fetch transactions using unified endpoint
  const fetchTransactions = useCallback(async () => {
    if (!organizationId) return;

    try {
      setTransactionsLoading(true);

      // Fetch all transactions from unified endpoint (filter client-side for immediate response)
      const txRes = await fetch(
        `${FUNCTIONS_BASE_URL}/appCreditsTransactions?organizationId=${organizationId}&limit=100`
      );
      const txData = await txRes.json();
      if (txData.success) {
        setTransactions(txData.transactions);
      }
    } catch (error) {
      console.error('Error fetching credits transactions:', error);
      toast.error('Erreur lors du chargement des transactions');
    } finally {
      setTransactionsLoading(false);
    }
  }, [organizationId]);

  // Filter transactions client-side for immediate responsiveness
  const filteredTransactions = useMemo(() => {
    if (usageTypeFilter === 'all') {
      return transactions;
    }
    // When filtering by usage type, only show transactions of that specific type
    return transactions.filter((tx) => {
      // Only filter usage transactions by usageType
      if (tx.type === 'usage') {
        return tx.usageType === usageTypeFilter;
      }
      // Don't show purchases or refunds when filtering by usage type
      return false;
    });
  }, [transactions, usageTypeFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleOpenPurchaseModal = (amountCents?: number) => {
    setInitialPurchaseAmount(amountCents);
    setShowPurchaseModal(true);
  };

  const handlePurchaseSuccess = () => {
    // Balance updates automatically via real-time listener
    // Just refresh transactions
    fetchTransactions();
  };

  const handleClosePurchaseModal = () => {
    setShowPurchaseModal(false);
    setInitialPurchaseAmount(undefined);
  };

  // Get usage type icon and color
  const getUsageTypeDisplay = (usageType?: 'shipping' | 'ai') => {
    if (usageType === 'shipping') {
      return {
        icon: <Truck className="h-3 w-3" />,
        label: t('admin.credits.usageTypes.shipping'),
        color: 'text-primary bg-primary-10',
      };
    }
    if (usageType === 'ai') {
      return {
        icon: <Bot className="h-3 w-3" />,
        label: t('admin.credits.usageTypes.ai'),
        color: 'text-secondary bg-secondary-10',
      };
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background-secondary">
      <SeoTags
        title="Crédits | CashLive"
        description="Gérez vos crédits pour l'expédition et l'IA"
        url={window.location.href}
        noIndex={true}
      />

      {/* Breadcrumb Navigation */}
      <div className="bg-surface border-b border-border shadow-sm">
        <div className="py-3 sm:py-4 px-4 sm:px-6">
          <nav className="flex items-center justify-start flex-wrap gap-2 text-xs sm:text-sm">
            <Link
              to="/organizations/manage"
              className="flex items-center space-x-1.5 px-2 py-1.5 rounded-lg text-primary hover:bg-primary-10 transition-colors font-medium"
            >
              <Home className="w-4 h-4" />
              <span className="hidden xs:inline">Organisations</span>
            </Link>
            <ChevronLeft className="w-4 h-4 text-foreground-secondary rotate-180" />
            <Link
              to={`/orga/${idorga}/admin`}
              className="text-primary hover:underline font-medium"
            >
              Dashboard
            </Link>
            <ChevronLeft className="w-4 h-4 text-foreground-secondary rotate-180" />
            <span className="text-foreground font-semibold">
              Crédits
            </span>
          </nav>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          {/* Colonne gauche - Balance Card + Rechargement rapide */}
          <div className="space-y-4 sm:space-y-6">
            {/* Balance Card */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-primary-dark" />
              <div
                className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl"
                style={{ backgroundColor: 'color-mix(in srgb, var(--surface) 10%, transparent)' }}
              />
              <div
                className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full blur-2xl"
                style={{ backgroundColor: 'color-mix(in srgb, var(--surface) 5%, transparent)' }}
              />

              <div className="relative px-4 sm:px-8 py-6 sm:py-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div
                      className="p-3.5 rounded-2xl backdrop-blur-md shadow-lg flex-shrink-0"
                      style={{ backgroundColor: 'color-mix(in srgb, var(--surface) 20%, transparent)' }}
                    >
                      <Wallet className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                    </div>
                    <div>
                      <p className="text-white/80 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1.5">
                        Solde disponible
                      </p>
                      <div className="flex items-baseline gap-2">
                        {loading ? (
                          <Loader2 className="h-8 w-8 text-white animate-spin" />
                        ) : (
                          <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight tabular-nums">
                            {formatCurrency(balance?.balanceCents ?? 0)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleOpenPurchaseModal()}
                    size="lg"
                    leftIcon={<Plus className="h-5 w-5" />}
                    className="bg-white text-primary hover:bg-white/90 font-bold shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] w-full md:w-auto h-12 px-8 rounded-xl"
                  >
                    Recharger
                  </Button>
                </div>

                {/* Stats grid with better responsiveness */}
                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4">
                  <div
                    className="backdrop-blur-md rounded-xl p-3 sm:p-4 border transition-all hover:bg-white/10"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--surface) 10%, transparent)',
                      borderColor: 'color-mix(in srgb, var(--surface) 10%, transparent)'
                    }}
                  >
                    <div className="flex items-center gap-2 text-white/70 text-[10px] font-bold uppercase tracking-wider mb-2">
                      <ArrowUpRight className="h-3 w-3" />
                      Total acheté
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-white tabular-nums">
                      {formatCurrency(balance?.totalPurchasedCents ?? 0)}
                    </p>
                  </div>
                  <div
                    className="backdrop-blur-md rounded-xl p-3 sm:p-4 border transition-all hover:bg-white/10"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--surface) 10%, transparent)',
                      borderColor: 'color-mix(in srgb, var(--surface) 10%, transparent)'
                    }}
                  >
                    <div className="flex items-center gap-2 text-white/70 text-[10px] font-bold uppercase tracking-wider mb-2">
                      <ArrowDownLeft className="h-3 w-3" />
                      Total utilisé
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-white tabular-nums">
                      {formatCurrency(balance?.totalUsedCents ?? 0)}
                    </p>
                  </div>
                  <div
                    className="backdrop-blur-md rounded-xl p-3 sm:p-4 border transition-all hover:bg-white/10"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--surface) 10%, transparent)',
                      borderColor: 'color-mix(in srgb, var(--surface) 10%, transparent)'
                    }}
                  >
                    <div className="flex items-center gap-2 text-white/70 text-[10px] font-bold uppercase tracking-wider mb-2">
                      <Truck className="h-3 w-3" />
                      Expédition
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-white tabular-nums">
                      {formatCurrency(balance?.totalUsedForShippingCents ?? 0)}
                    </p>
                  </div>
                  <div
                    className="backdrop-blur-md rounded-xl p-3 sm:p-4 border transition-all hover:bg-white/10"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--surface) 10%, transparent)',
                      borderColor: 'color-mix(in srgb, var(--surface) 10%, transparent)'
                    }}
                  >
                    <div className="flex items-center gap-2 text-white/70 text-[10px] font-bold uppercase tracking-wider mb-2">
                      <Bot className="h-3 w-3" />
                      IA
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-white tabular-nums">
                      {formatCurrency(balance?.totalUsedForAICents ?? 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Purchase Buttons */}
            <Card variant="default">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
                    <Plus className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Rechargement rapide</h3>
                    <p className="text-xs text-foreground-secondary mt-0.5">
                      Choisissez un montant pour recharger rapidement
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <button
                    onClick={() => handleOpenPurchaseModal(1000)}
                    className="px-4 py-3 sm:py-3.5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary-10 transition-all font-semibold text-foreground hover:shadow-md active:scale-95 text-sm sm:text-base"
                  >
                    10€
                  </button>
                  <button
                    onClick={() => handleOpenPurchaseModal(5000)}
                    className="px-4 py-3 sm:py-3.5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary-10 transition-all font-semibold text-foreground hover:shadow-md active:scale-95 text-sm sm:text-base"
                  >
                    50€
                  </button>
                  <button
                    onClick={() => handleOpenPurchaseModal(10000)}
                    className="px-4 py-3 sm:py-3.5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary-10 transition-all font-semibold text-foreground hover:shadow-md active:scale-95 text-sm sm:text-base"
                  >
                    100€
                  </button>
                  <button
                    onClick={() => handleOpenPurchaseModal(50000)}
                    className="px-4 py-3 sm:py-3.5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary-10 transition-all font-semibold text-foreground hover:shadow-md active:scale-95 text-sm sm:text-base"
                  >
                    500€
                  </button>
                </div>
              </CardBody>
            </Card>

            {/* Transaction History */}
            <Card variant="default">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
                      <History className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Historique des transactions</h3>
                      <p className="text-xs text-foreground-secondary mt-0.5">
                        {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
                        {usageTypeFilter !== 'all' && ` (${transactions.length} au total)`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Usage Type Filter */}
                    <div className="flex items-center gap-1 bg-background-secondary rounded-lg p-1">
                      <button
                        onClick={() => setUsageTypeFilter('all')}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${usageTypeFilter === 'all'
                          ? 'bg-surface shadow-sm text-foreground'
                          : 'text-foreground-secondary hover:text-foreground'
                          }`}
                      >
                        Tout
                      </button>
                      <button
                        onClick={() => setUsageTypeFilter('shipping')}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${usageTypeFilter === 'shipping'
                          ? 'bg-surface shadow-sm text-primary'
                          : 'text-foreground-secondary hover:text-foreground'
                          }`}
                      >
                        <Truck className="h-3 w-3" />
                        {t('admin.credits.usageTypes.shipping')}
                      </button>
                      <button
                        onClick={() => setUsageTypeFilter('ai')}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${usageTypeFilter === 'ai'
                          ? 'bg-surface shadow-sm text-secondary'
                          : 'text-foreground-secondary hover:text-foreground'
                          }`}
                      >
                        <Bot className="h-3 w-3" />
                        IA
                      </button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardBody>

                {transactionsLoading && transactions.length === 0 ? (
                  <div className="p-12 text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                    <p className="text-foreground-secondary">Chargement...</p>
                  </div>
                ) : filteredTransactions.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="inline-flex p-4 rounded-full bg-background-secondary mb-4">
                      <Clock className="h-8 w-8 text-foreground-secondary" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-1">Aucune transaction</h3>
                    <p className="text-sm text-foreground-secondary">
                      {usageTypeFilter !== 'all'
                        ? t('admin.credits.filter.noTransactions', { type: usageTypeFilter === 'shipping' ? t('admin.credits.usageTypes.shipping') : t('admin.credits.usageTypes.ai') })
                        : t('admin.credits.noTransactionsYet', 'Achetez des crédits pour commencer')
                      }
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredTransactions.map((tx) => {
                      const usageDisplay = getUsageTypeDisplay(tx.usageType);
                      const isPositive = tx.type === 'purchase' || tx.type === 'refund';

                      return (
                        <div key={tx.id} className="py-4 hover:bg-background-secondary/50 transition-all px-2 -mx-2 rounded-lg">
                          <div className="flex items-start sm:items-center justify-between gap-4">
                            <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                              {/* Icon */}
                              <div
                                className={`p-2 rounded-xl flex-shrink-0 shadow-sm ${tx.type === 'purchase'
                                  ? 'bg-success-10 text-success'
                                  : tx.type === 'refund'
                                    ? 'bg-primary-10 text-primary'
                                    : 'bg-warning-10 text-warning'
                                  }`}
                              >
                                {tx.type === 'purchase' ? (
                                  <Plus className="h-4 w-4" />
                                ) : tx.type === 'refund' ? (
                                  <RefreshCw className="h-4 w-4" />
                                ) : (
                                  <Minus className="h-4 w-4" />
                                )}
                              </div>

                              {/* Main content */}
                              <div className="flex-1 min-w-0 pt-0.5 sm:pt-0">
                                <p className="text-sm font-semibold text-foreground truncate sm:whitespace-normal mb-1">
                                  {tx.usageType === 'ai' ? formatAIDescription(tx.description) : tx.description}
                                </p>
                                <div className="flex flex-wrap items-center gap-y-1 gap-x-3 text-xs text-foreground-secondary font-medium">
                                  <span className="flex items-center gap-1.5">
                                    <Clock className="h-3 w-3" />
                                    {tx.createdAt && formatDate(tx.createdAt)}
                                  </span>
                                  {usageDisplay && tx.type === 'usage' && (
                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-opacity-10 ${usageDisplay.color.replace('text-', 'bg-').replace('text-', '')} font-bold uppercase tracking-wider text-[9px]`}>
                                      {usageDisplay.icon}
                                      {usageDisplay.label}
                                    </span>
                                  )}
                                  {tx.parcelsCreated && (
                                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-primary-10 text-primary font-bold text-[9px] uppercase tracking-wider">
                                      <Package className="h-2.5 w-2.5" />
                                      {tx.parcelsCreated} colis
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Amount */}
                            <div className="text-right flex-shrink-0 pt-0.5 sm:pt-0">
                              <p
                                className={`font-bold text-sm sm:text-base tabular-nums ${isPositive ? 'text-success' : 'text-warning'
                                  }`}
                              >
                                {isPositive ? '+' : ''}
                                {formatCurrency(tx.amountCents)}
                              </p>
                              <p className="text-[10px] text-foreground-secondary mt-0.5 font-medium">
                                {formatCurrency(tx.balanceAfterCents)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Colonne droite - Info Card + Transaction History */}
          <div className="space-y-6">
            {/* Info Card - Enhanced */}
            {showInfoCard && (
              <div className="relative overflow-hidden rounded-2xl border border-border bg-surface shadow-lg">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary via-secondary to-accent px-4 sm:px-6 py-4 sm:py-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-sm">
                      <Wallet className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">
                        Comment fonctionnent les crédits ?
                      </h3>
                      <p className="text-white/90 text-sm mt-0.5">
                        Système de paiement prépayé pour vos services
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content - Stacked vertically for narrow column */}
                <div className="space-y-0 divide-y divide-border-light">
                  {/* Achat de crédits */}
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 rounded-xl bg-success-10 text-success">
                        <Plus className="h-5 w-5" />
                      </div>
                      <h4 className="font-semibold text-foreground text-base">Achat de crédits</h4>
                    </div>
                    <ul className="space-y-2.5 text-sm text-foreground-secondary">
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" />
                        <span>Rechargez votre compte à partir de <strong>10€</strong></span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" />
                        <span>{t('admin.credits.sections.securePayment', 'Paiement sécurisé par carte bancaire')}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: t('admin.credits.sections.instantCredits', 'Crédits ajoutés <strong>instantanément</strong>') }} />
                      </li>
                    </ul>
                  </div>

                  {/* Expédition */}
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 rounded-xl bg-primary-10 text-primary">
                        <Truck className="h-5 w-5" />
                      </div>
                      <h4 className="font-semibold text-foreground text-base">{t('admin.credits.sections.shippingTitle')}</h4>
                    </div>
                    <p className="text-sm text-foreground-secondary leading-relaxed" dangerouslySetInnerHTML={{ __html: t('admin.credits.sections.shippingDescription') }} />

                    {/* Stripe Connect Notice */}
                    {stripeConnectEnabled && (
                      <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <div className="flex items-start gap-2">
                          <CreditCard className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-blue-700 leading-relaxed">
                              En Stripe Connect, les frais de livraison sont <strong>prélevés automatiquement</strong>. Pas besoin d'utiliser les crédits.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {!stripeConnectEnabled && (
                      <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <div className="flex items-start gap-2">
                          <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-amber-800 leading-relaxed">
                              Sans Stripe Connect, les crédits sont nécessaires pour générer automatiquement vos colis.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Assistant IA */}
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2.5 rounded-xl bg-secondary-10 text-secondary">
                        <Bot className="h-5 w-5" />
                      </div>
                      <h4 className="font-semibold text-foreground text-base">{t('admin.credits.sections.aiTitle')}</h4>
                    </div>
                    <ul className="space-y-2.5 text-sm text-foreground-secondary">
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 shrink-0" />
                        <span>{t('admin.credits.sections.aiDescription')}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: t('admin.credits.sections.assistantConsumption') }} />
                      </li>
                      <li className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 shrink-0" />
                        <span>{t('admin.credits.sections.automaticResponses')}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Footer tip */}
                <div className="px-5 py-3 bg-background-secondary border-t border-border-light">
                  <p className="text-xs text-foreground-secondary flex items-center gap-2">
                    <HelpCircle className="h-3.5 w-3.5" />
                    <span dangerouslySetInnerHTML={{ __html: t('admin.credits.sections.neverExpires') }} />
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      {organizationId && (
        <BuyCreditsModal
          isOpen={showPurchaseModal}
          onClose={handleClosePurchaseModal}
          organizationId={organizationId}
          currentBalance={balance?.balanceCents ?? 0}
          onSuccess={handlePurchaseSuccess}
          initialAmount={initialPurchaseAmount}
        />
      )}
    </div>
  );
};

export default ShippingCreditsPage;
